/**
 * Global online presence tracker via Socket.IO.
 * Provides a Set<user_id> of currently online users + helpers.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const PresenceContext = createContext({ onlineSet: new Set(), isOnline: () => false, emitTyping: () => {}, onTyping: () => () => {}, joinRoom: () => {}, leaveRoom: () => {} });

export const PresenceProvider = ({ user, children }) => {
  const [onlineSet, setOnlineSet] = useState(() => new Set());
  const socketRef = useRef(null);
  const typingListenersRef = useRef(new Set());
  // Track which rooms we've asked to join, so we can re-join after a reconnect.
  const joinedRoomsRef = useRef(new Set());

  useEffect(() => {
    if (!user) {
      setOnlineSet(new Set());
      return;
    }

    const BACKEND_URL = window.location.origin;
    const socket = io(BACKEND_URL, {
      path: '/api/sio/',
      transports: ['polling', 'websocket'],
      reconnection: true,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('register_user', {
        user_id: user.user_id,
        gamertag: user.gamertag,
        name: user.name,
        role: user.role,
      });
      // Re-join any rooms we had previously joined (e.g. after reconnect).
      joinedRoomsRef.current.forEach(room => {
        try { socket.emit('join_room', { room }); } catch { /* ignore */ }
      });
    });

    socket.on('online_users', (users) => {
      setOnlineSet(new Set((users || []).map(u => u.user_id).filter(Boolean)));
    });

    socket.on('user_online', ({ user_id }) => {
      if (!user_id) return;
      setOnlineSet(prev => {
        if (prev.has(user_id)) return prev;
        const next = new Set(prev);
        next.add(user_id);
        return next;
      });
    });

    socket.on('user_offline', ({ user_id }) => {
      if (!user_id) return;
      setOnlineSet(prev => {
        if (!prev.has(user_id)) return prev;
        const next = new Set(prev);
        next.delete(user_id);
        return next;
      });
    });

    // Typing relay: forward to all registered listeners
    socket.on('typing', (payload) => {
      typingListenersRef.current.forEach(fn => {
        try { fn(payload); } catch { /* ignore */ }
      });
    });

    return () => {
      try { socket.disconnect(); } catch { /* ignore */ }
      socketRef.current = null;
    };
  }, [user]);

  const isOnline = useCallback((uid) => onlineSet.has(uid), [onlineSet]);

  const emitTyping = useCallback((room, state) => {
    const s = socketRef.current;
    if (!s || !user) return;
    try {
      s.emit('typing', { room, user_id: user.user_id, gamertag: user.gamertag, state });
    } catch { /* ignore */ }
  }, [user]);

  const onTyping = useCallback((listener) => {
    typingListenersRef.current.add(listener);
    return () => { typingListenersRef.current.delete(listener); };
  }, []);

  // Ask the presence socket to join a chat room so it can receive
  // room-scoped events (typing, read receipts, etc.). Idempotent.
  const joinRoom = useCallback((room) => {
    if (!room) return;
    joinedRoomsRef.current.add(room);
    const s = socketRef.current;
    if (s && s.connected) {
      try { s.emit('join_room', { room }); } catch { /* ignore */ }
    }
  }, []);

  const leaveRoom = useCallback((room) => {
    if (!room) return;
    joinedRoomsRef.current.delete(room);
    const s = socketRef.current;
    if (s && s.connected) {
      try { s.emit('leave_room', { room }); } catch { /* ignore */ }
    }
  }, []);

  return (
    <PresenceContext.Provider value={{ onlineSet, isOnline, emitTyping, onTyping, joinRoom, leaveRoom }}>
      {children}
    </PresenceContext.Provider>
  );
};

export const usePresence = () => useContext(PresenceContext);
